import { errorMessage, type ApiErrorBody, type Locale, type TranslationKey } from "@planner/core";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocale } from "../i18n/provider.js";
import { platform } from "../platform/index.js";
import { faultToError } from "./Crash.js";
import { interceptStepUp } from "./StepUp.js";
import { Icon } from "./icons.js";

/**
 * الرسالة اللحظية — **أحد مخرجات المسار الواحد، لا رسالة تُكتب بيد**.
 *
 * لا يستقبل هذا المضيف نصّاً أبداً. يستقبل **نتيجة عملية**: إمّا تغذيةً راجعة
 * أعلنتها العملية في سجلّها، وإمّا جسم خطأ. ولهذا لا يمكن أن تُنسى رسالة نجاح
 * ولا أن تظهر رسالة خطأ بلا كود — الاثنتان تأتيان من الإعلان نفسه الذي كتب
 * صفّ التدقيق وأطلق الإشعار.
 */

export type ToastTone = "ok" | "error";

export type Toast = {
  readonly id: string;
  readonly messageKey: TranslationKey;
  readonly tone: ToastTone;
  /** سياق الرسالة — يملأ فراغاتها، فلا تصل بالأقواس حرفياً */
  readonly context?: Readonly<Record<string, unknown>>;
  /** يظهر على التوست الخطأ ليقوله المستخدم كما هو */
  readonly correlationId?: string;
  readonly code?: string;
};

type ToastContextValue = {
  /** نجاحٌ كما أعلنته العملية — أو لا شيء إن كانت صامتةً بقرار */
  readonly succeeded: (
    feedback: {
      readonly messageKey: TranslationKey;
      readonly params?: Readonly<Record<string, string>>;
    } | null,
    correlationId: string,
  ) => void;
  /**
   * فشلٌ بجسمه الكامل — الكود ورقم الربط يظهران للمستخدم.
   *
   * **و`retry` يُمرَّر حين تكون إعادةُ النداء ممكنةً بلا إدخالٍ جديد**: فطلبُ
   * تحقّقٍ ثانٍ يُعترَض هنا، وتُعاد العمليةُ نفسُها بعده بلا أن يلمس المستخدم
   * شيئاً. وبدونه تُفتَح النافذةُ كذلك، ويُقال له أن يضغط ثانية.
   */
  readonly failed: (error: ApiErrorBody, retry?: () => void) => void;
  readonly dismiss: (id: string) => void;
  readonly toasts: readonly Toast[];
};

const ToastContext = createContext<ToastContextValue | null>(null);

/** لا يُعرَض للمستخدم كخطأ — ألغاه هو أو ألغته الشاشة */
const SILENT_CODES = new Set(["REQUEST_ABORTED"]);

const VISIBLE_MS = 5000;
/**
 * **والخطأ يبقى ثلاثةَ أضعاف** — وقتاً يكفي لقراءة كوده ونسخِه (قرار شادي ٢٠٢٦-١١-٠٢).
 *
 * كان يبقى حتى يُغلَق، **فيغطّي الشاشةَ فوق ما يُعمَل بعده**. والكودُ لا يضيع إن مضى:
 * الفشلُ مكتوبٌ في سجلّ الأخطاء برقم ربطه نفسِه. **وما دامت اليدُ عليه لا يمضي.**
 */
const ERROR_VISIBLE_MS = 15_000;

export function ToastProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const counter = useRef(0);

  const push = useCallback((toast: Omit<Toast, "id">) => {
    counter.current += 1;
    const id = `t${counter.current}`;
    setToasts((current) => {
      /**
       * **خطأٌ بنفس الكود يُستبدَل ولا يتكدّس.**
       *
       * فالخطأ **لا يمضي وحده** عمداً — وهو قرارٌ صحيح. لكنّ ضغطَ زرٍّ مكسورٍ
       * مرّتين كان يُنتج بطاقتين، وثلاثاً، حتى تغطّي الشاشة. ورقمُ الربط الذي
       * يُقتبَس هو **الأحدث**، فيبقى هو.
       *
       * ويبقى خطأٌ بكودٍ آخر على حدة: اختلافُ الكود اختلافُ سببٍ يُقرأ.
       */
      const withoutTwin =
        toast.code !== undefined ? current.filter((t) => t.code !== toast.code) : current;
      return [...withoutTwin, { ...toast, id }];
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      dismiss,
      succeeded: (feedback) => {
        // صامتة بقرار: الشاشة تُظهر النتيجة بنفسها، فلا توست فوقها
        if (!feedback) return;
        /**
         * **وما أعلنته العملية يملأ فراغاتها.**
         *
         * وكان `context` مبنيّاً هنا **للخطأ وحده**، فثلاثةُ توستات نجاحٍ
         * مُعلَنةٍ تحمل `{name}` في القاموس كانت تُعرَض بالأقواس حرفياً:
         * «`{name}` بقى في فريق العميل». رسالةٌ تُقال ولا تُفهَم، في قناةٍ
         * مبنيّةٍ كلُّها لتُفهَم.
         */
        push({
          messageKey: feedback.messageKey,
          tone: "ok",
          ...(feedback.params ? { context: feedback.params } : {}),
        });
      },
      failed: (error, retry) => {
        if (SILENT_CODES.has(error.code)) return;
        /**
         * **وطلبُ التحقّق الثاني ليس عطباً** — فلا يُعرَض توستَ خطأ.
         *
         * ويُعترَض هنا لأن **كلَّ فشلٍ في النظام يمرّ بهذا المضيف**: اثنان
         * وعشرون موضعَ نداءٍ في ثلاثةَ عشرَ ملفّاً، ولو كُتب في كلٍّ منها
         * لنُسي في واحد — وهو بالضبط ما سيُستعمَل.
         */
        if (interceptStepUp(error, retry)) return;
        push({
          messageKey: error.messageKey,
          tone: "error",
          correlationId: error.correlationId,
          code: error.code,
          ...(error.context ? { context: error.context } : {}),
        });
      },
    }),
    [toasts, dismiss, push],
  );

  /**
   * **وعطبٌ لم يمسكه أحدٌ يصل إلى هنا** — بكوده ومرجعه، كأيّ فشلٍ آخر.
   *
   * فوعدٌ مرفوضٌ داخل تأثيرٍ بلا `catch` كان يترك الشاشة تدور إلى الأبد:
   * الطلبُ نجح، والكودُ بعده رمى، **ولا شيء في الدنيا يقول ذلك للمستخدم**.
   * وهنا يصير له بطاقةٌ تحمل `INTERNAL` ومرجعاً يُقتبَس.
   *
   * ويُعرَض توستاً لا شاشةً كاملة: **الشجرةُ ما زالت حيّة** وما كان معروضاً
   * قبل العطب يبقى مقروءاً — والسقوطُ الكامل وحده يملك `CrashBoundary`.
   */
  useEffect(
    () =>
      platform.onUnhandledFault((cause) => {
        value.failed(faultToError(cause));
      }),
    [value],
  );

  return (
    <ToastContext value={value}>
      {children}
      <ToastViewport />
    </ToastContext>
  );
}

export function useToast(): ToastContextValue {
  const value = use(ToastContext);
  if (!value) throw new Error("useToast used outside ToastProvider");
  return value;
}

function ToastViewport(): ReactNode {
  const { toasts, dismiss } = useToast();
  const { t, locale } = useLocale();
  return (
    <div className="tst" role="region" aria-label={t("shell.notificationsRegion")}>
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} onDismiss={dismiss} locale={locale} />
      ))}
    </div>
  );
}

function ToastRow({
  toast,
  onDismiss,
  locale,
}: {
  readonly toast: Toast;
  readonly onDismiss: (id: string) => void;
  readonly locale: Locale;
}): ReactNode {
  const { t } = useLocale();
  /** **ما دام المؤشّرُ أو التركيزُ عليه لا يمضي** — فمن يقرأ الكودَ لا يُسحَب منه */
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (held) return;
    const timer = setTimeout(
      () => {
        onDismiss(toast.id);
      },
      toast.tone === "error" ? ERROR_VISIBLE_MS : VISIBLE_MS,
    );
    return () => {
      clearTimeout(timer);
    };
  }, [toast, onDismiss, held]);

  return (
    <output
      className={toast.tone === "error" ? "t1 err" : "t1"}
      onPointerEnter={() => {
        setHeld(true);
      }}
      onPointerLeave={() => {
        setHeld(false);
      }}
      onFocus={() => {
        setHeld(true);
      }}
      onBlur={() => {
        setHeld(false);
      }}
    >
      {/**
       * **الرسالةُ سطرٌ كامل، والكودان تحتها** — وكانوا ثلاثتُهم في صفٍّ واحد:
       * رقمُ الربط ستّةٌ وثلاثون حرفاً لا ينكسر، **فتنعصر الرسالةُ كلمةً في كلّ سطر**
       * على الشاشة الكبيرة وعلى الموبايل معاً.
       */}
      <span className="t1-body">
        <span className="t1-text">{errorMessage(locale, toast)}</span>
        {toast.code || toast.correlationId ? (
          <span className="t1-codes">
            {toast.code ? (
              <span className="code" data-testid="toast-code">
                {toast.code}
              </span>
            ) : null}
            {toast.correlationId ? (
              <span className="code" data-testid="toast-correlation">
                {toast.correlationId}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
      <button
        type="button"
        className="t1-x"
        aria-label={t("common.close")}
        onClick={() => {
          onDismiss(toast.id);
        }}
      >
        <Icon name="close" />
      </button>
    </output>
  );
}
